import { ApiError } from "../api.js";
import { verifyEmail, resendVerification, isAuthenticated } from "../auth.js";
import { esc } from "../utils.js";
import { navigate } from "../app.js";
import { takeReturn } from "../returnTo.js";

/** What we've done with a token this page-load:
 *   { token, state: "pending" | "ok" | "error", message }
 *
 * Needed because a successful confirm updates the cached session user
 * and notifies auth listeners, which makes app.js re-run renderRoute()
 * — and the route is still #/verify/<token> at that moment, so this view
 * re-enters itself and POSTs again, without bound.
 *
 * The "pending" state is the load-bearing part: auth.js fires that
 * notification from *inside* verifyEmail(), while this function is still
 * suspended on its await. A guard recorded only after the await would
 * never be reached — the re-entrant call arrives first. So the token is
 * claimed before the request goes out, and the re-entrant render returns
 * immediately, leaving the original call to paint the result.
 *
 * Confirming is idempotent server-side, so the loop never corrupted
 * anything; it just hammered the API and never settled. */
let settled = null;

/** Landing page for the link in the confirmation email
 * (#/verify/<token>). Unprotected: the signed token is the credential,
 * and mail clients routinely open links in a browser with no session. */
export async function render(container, params) {
  const token = params?.token;

  if (!token) {
    paintFailure(container, "That confirmation link is incomplete.");
    return;
  }

  if (settled && settled.token === token) {
    // Re-entered while the confirm is still in flight: the spinner is
    // already on screen and the original call will paint. Do nothing.
    if (settled.state === "pending") return;
    if (settled.state === "ok") paintSuccess(container);
    else paintFailure(container, settled.message);
    return;
  }

  // Claim the token *before* awaiting, not after — see the note above.
  settled = { token, state: "pending" };

  container.innerHTML = `
    <div class="text-center py-5">
      <div class="spinner-border text-primary"></div>
      <p class="text-slate mt-3">Confirming your email address...</p>
    </div>`;

  try {
    await verifyEmail(token);
    settled = { token, state: "ok" };
    paintSuccess(container);
  } catch (err) {
    const message = err instanceof ApiError ? err.message : "Could not confirm this link.";
    settled = { token, state: "error", message };
    paintFailure(container, message);
  }
}

function paintSuccess(container) {
  container.innerHTML = `
    <div class="dc-empty text-center py-5">
      <i class="bi bi-check-circle text-success" style="font-size:2.5rem;"></i>
      <h1 class="h4 mt-3">Email confirmed</h1>
      <p class="text-slate">Your account is ready — you can post, join cohorts, and RSVP to dives.</p>
      <button class="btn btn-primary mt-2" id="verify-continue">
        ${isAuthenticated() ? "Go to the feed" : "Sign in"}
      </button>
    </div>`;
  const button = container.querySelector("#verify-continue");
  if (button) {
    button.addEventListener("click", () => {
      navigate(isAuthenticated() ? takeReturn("/feed") : "/login");
    });
  }
}

function paintFailure(container, message) {
  // Offer a resend only when signed in — /auth/resend-verification
  // identifies the account from the JWT, so there is nobody to resend
  // to otherwise.
  container.innerHTML = `
    <div class="dc-empty text-center py-5">
      <i class="bi bi-exclamation-triangle text-warning" style="font-size:2.5rem;"></i>
      <h1 class="h4 mt-3">Couldn't confirm your email</h1>
      <p class="text-slate">${esc(message)}</p>
      <div class="d-flex gap-2 justify-content-center mt-2">
        ${isAuthenticated()
          ? `<button class="btn btn-primary" id="verify-resend">Send a new link</button>`
          : `<button class="btn btn-primary" id="verify-login">Sign in</button>`}
      </div>
      <div id="verify-resend-status" class="small mt-3"></div>
    </div>`;

  const loginBtn = container.querySelector("#verify-login");
  if (loginBtn) loginBtn.addEventListener("click", () => navigate("/login"));

  const resendBtn = container.querySelector("#verify-resend");
  if (resendBtn) {
    resendBtn.addEventListener("click", async () => {
      const status = container.querySelector("#verify-resend-status");
      resendBtn.disabled = true;
      status.className = "small mt-3 text-slate";
      status.textContent = "Sending...";
      try {
        await resendVerification();
        status.className = "small mt-3 text-success";
        status.textContent = "Sent — check your inbox for a fresh link.";
      } catch (err) {
        status.className = "small mt-3 text-danger";
        status.textContent =
          err instanceof ApiError ? err.message : "Could not send a new link.";
        resendBtn.disabled = false;
      }
    });
  }
}
